import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { Trash2 } from 'lucide-react';
import { db } from '@/firebase';
import './OrderTaskPrint.css';
import { DEFAULT_ORDER_PRINT_SETTINGS, getOrderPrintSettings, type OrderPrintSettings } from '@/utils/orderPrintSettings';

interface ExamScore {
    label: string;
    score: number | null;
    max: number;
}

interface ExamReview {
    chapter: number;
    qno: number;
    explanation: string;
    explanationHeight?: number;
    pageBreakBefore?: boolean;
}

interface QuestionItem {
    id: string;
    Chapter: number;
    Qno: number;
    Explanation?: string;
}

type SaveStatus = 'draft' | 'saved';
type ActiveTab = 'exam' | 'letter';
type PrintMode = 'all' | 'main' | 'letter';

const SCORE_ROW_COUNT = 5;
const DEFAULT_SCORE_MAX = 20;
const EXAM_CHAPTER_OPTIONS = [1, 2, 3, 4, 5];
const EXAM_QNO_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    const savedHeight = Number(textarea.dataset.savedHeight || 0);
    const nextHeight = Math.max(textarea.scrollHeight, textarea.offsetHeight, savedHeight);
    textarea.style.height = `${nextHeight}px`;
};

const createDefaultScores = (count: number): ExamScore[] =>
    Array.from({ length: count }, (_, index) => ({
        label: `問題${index + 1}`,
        score: null,
        max: DEFAULT_SCORE_MAX,
    }));

const normalizeScores = (scores: ExamScore[]) => {
    const base = Array.from({ length: SCORE_ROW_COUNT }, (_, index) => ({
        label: `問題${index + 1}`,
        score: null,
        max: DEFAULT_SCORE_MAX,
    }));
    return base.map((fallback, index) => {
        const existing = scores[index];
        return existing
            ? {
                  ...fallback,
                  ...existing,
                  label: existing.label || fallback.label,
              }
            : fallback;
    });
};

const sumScores = (scores: ExamScore[]) =>
    scores.reduce(
        (acc, item) => ({
            score: acc.score + (Number.isFinite(item.score) ? item.score : 0),
            max: acc.max + (Number.isFinite(item.max) ? item.max : 0),
        }),
        { score: 0, max: 0 }
    );

const sanitizeExamReviews = (reviews: ExamReview[]): ExamReview[] =>
    reviews.map((review) => {
        const sanitized = { ...review } as Record<string, unknown>;
        Object.keys(sanitized).forEach((key) => {
            if (sanitized[key] === undefined) {
                delete sanitized[key];
            }
        });
        return sanitized as ExamReview;
    });

export default function OrderExamPrint() {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [orderData, setOrderData] = useState<any>(null);
    const [questionItems, setQuestionItems] = useState<QuestionItem[]>([]);
    const [draftExamScores, setDraftExamScores] = useState<ExamScore[]>([]);
    const [draftExamReviews, setDraftExamReviews] = useState<ExamReview[]>([]);
    const [savedExamScores, setSavedExamScores] = useState<ExamScore[]>([]);
    const [savedExamReviews, setSavedExamReviews] = useState<ExamReview[]>([]);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('draft');
    const [shikakuData, setShikakuData] = useState<any>(null);
    const [userName, setUserName] = useState<string>('');
    const [activeTab, setActiveTab] = useState<ActiveTab>('exam');
    const [printMode, setPrintMode] = useState<PrintMode>('all');
    const [isPrintMenuOpen, setIsPrintMenuOpen] = useState(false);
    const [orderPrintSettings, setOrderPrintSettings] = useState<OrderPrintSettings>(DEFAULT_ORDER_PRINT_SETTINGS);
    const [searchParams] = useSearchParams();
    const reviewContainerRef = useRef<HTMLElement | null>(null);
    const hasAutoPrinted = useRef(false);

    useEffect(() => {
        document.body.classList.add('order-task-print');
        return () => {
            document.body.classList.remove('order-task-print');
        };
    }, []);

    useEffect(() => {
        const handleAfterPrint = () => {
            setPrintMode('all');
        };
        window.addEventListener('afterprint', handleAfterPrint);
        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    useEffect(() => {
        let active = true;

        const loadOrderPrintSettings = async () => {
            try {
                const data = await getOrderPrintSettings();
                if (active) {
                    setOrderPrintSettings(data);
                }
            } catch (error) {
                console.error('Error loading order print settings:', error);
            }
        };

        loadOrderPrintSettings();

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        const previousValues = {
            marginTopBottom: root.style.getPropertyValue('--order-print-page-margin-top-bottom'),
            paddingTop: root.style.getPropertyValue('--order-print-page-padding-top'),
            paddingBottom: root.style.getPropertyValue('--order-print-page-padding-bottom'),
            reviewFontSize: root.style.getPropertyValue('--order-print-review-font-size'),
            reviewCellPadding: root.style.getPropertyValue('--order-print-review-cell-padding'),
            reviewTextareaPadding: root.style.getPropertyValue('--order-print-review-textarea-padding'),
            taskLetterTitleFontSize: root.style.getPropertyValue('--order-print-task-letter-title-font-size'),
            examLetterTitleFontSize: root.style.getPropertyValue('--order-print-exam-letter-title-font-size'),
        };

        root.style.setProperty('--order-print-page-margin-top-bottom', `${orderPrintSettings.pageMarginTopBottomMm}mm`);
        root.style.setProperty('--order-print-page-padding-top', `${orderPrintSettings.pagePaddingTopMm}mm`);
        root.style.setProperty('--order-print-page-padding-bottom', `${orderPrintSettings.pagePaddingBottomMm}mm`);
        root.style.setProperty('--order-print-review-font-size', `${orderPrintSettings.reviewFontSizePt}pt`);
        root.style.setProperty('--order-print-review-cell-padding', `${orderPrintSettings.reviewCellPaddingPx}px`);
        root.style.setProperty('--order-print-review-textarea-padding', `${orderPrintSettings.reviewTextareaPaddingPx}px`);
        root.style.setProperty('--order-print-task-letter-title-font-size', `${orderPrintSettings.taskLetterTitleFontSizePt}pt`);
        root.style.setProperty('--order-print-exam-letter-title-font-size', `${orderPrintSettings.examLetterTitleFontSizePt}pt`);

        const pageMarginTopBottom = orderPrintSettings.pageMarginTopBottomMm;
        const pagePaddingTop = orderPrintSettings.pagePaddingTopMm;
        const pagePaddingBottom = orderPrintSettings.pagePaddingBottomMm;

        const existingStyleEl = document.getElementById('order-exam-print-page-rules');
        existingStyleEl?.remove();

        const styleEl = document.createElement('style');
        styleEl.id = 'order-exam-print-page-rules';
        styleEl.textContent = `
            @page {
                size: A4;
                margin: ${pageMarginTopBottom}mm 0;
            }

            @page order-main {
                size: A4;
                margin: ${pageMarginTopBottom + pagePaddingTop}mm 0 ${pageMarginTopBottom + pagePaddingBottom}mm;
            }

            @page order-letter {
                size: A4;
                margin: ${pageMarginTopBottom + pagePaddingTop}mm 0 ${pageMarginTopBottom + pagePaddingBottom}mm;
            }
        `;
        document.head.appendChild(styleEl);

        return () => {
            root.style.setProperty('--order-print-page-margin-top-bottom', previousValues.marginTopBottom);
            root.style.setProperty('--order-print-page-padding-top', previousValues.paddingTop);
            root.style.setProperty('--order-print-page-padding-bottom', previousValues.paddingBottom);
            root.style.setProperty('--order-print-review-font-size', previousValues.reviewFontSize);
            root.style.setProperty('--order-print-review-cell-padding', previousValues.reviewCellPadding);
            root.style.setProperty('--order-print-review-textarea-padding', previousValues.reviewTextareaPadding);
            root.style.setProperty('--order-print-task-letter-title-font-size', previousValues.taskLetterTitleFontSize);
            root.style.setProperty('--order-print-exam-letter-title-font-size', previousValues.examLetterTitleFontSize);
            styleEl.remove();
        };
    }, [orderPrintSettings]);

    useEffect(() => {
        const container = reviewContainerRef.current;
        if (!container) {
            return;
        }
        const textareas = container.querySelectorAll<HTMLTextAreaElement>('textarea[data-auto-resize]');
        textareas.forEach(autoResizeTextarea);
    }, [draftExamReviews, activeTab]);

    useEffect(() => {
        const handleBeforePrint = () => {
            const container = reviewContainerRef.current;
            if (!container) {
                return;
            }
            const textareas = container.querySelectorAll<HTMLTextAreaElement>('textarea[data-auto-resize]');
            textareas.forEach(autoResizeTextarea);
        };
        window.addEventListener('beforeprint', handleBeforePrint);
        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
        };
    }, []);

    useEffect(() => {
        const fetchOrder = async () => {
            if (!id) {
                setError('Order IDが指定されていません');
                setLoading(false);
                return;
            }

            try {
                const orderDoc = await getDoc(doc(db, 'Order', id));
                if (!orderDoc.exists()) {
                    setError('Orderが見つかりませんでした');
                    setLoading(false);
                    return;
                }

                const data = { orderId: orderDoc.id, ...orderDoc.data() };
                setOrderData(data);

                let shikakuRef = data.shikakuId;
                if (shikakuRef && !shikakuRef.path) {
                    shikakuRef = doc(db, 'Shikaku', shikakuRef);
                }

                if (shikakuRef) {
                    const shikakuDoc = await getDoc(shikakuRef);
                    if (shikakuDoc.exists()) {
                        setShikakuData(shikakuDoc.data());
                    }
                }

                const userRef = data.UserRef || data.userId;
                if (userRef) {
                    const userDocRef = userRef.path ? userRef : doc(db, 'User', userRef);
                    const userSnapshot = await getDoc(userDocRef);
                    if (userSnapshot.exists()) {
                        const userData = userSnapshot.data();
                        setUserName(userData.display_name || userData.display_Name || '');
                    }
                }

                const storedScores = Array.isArray(data.examScores) ? data.examScores : null;
                const storedReviews = Array.isArray(data.examReviews) ? data.examReviews : null;

                let questionData: QuestionItem[] = [];
                if (shikakuRef) {
                    const questionsQuery = query(collection(db, 'Questions'), where('SKref', '==', shikakuRef));
                    const questionsSnapshot = await getDocs(questionsQuery);
                    questionData = questionsSnapshot.docs
                        .map((questionDoc) => ({ id: questionDoc.id, ...(questionDoc.data() as QuestionItem) }))
                        .sort((a, b) => {
                            if (a.Chapter !== b.Chapter) return a.Chapter - b.Chapter;
                            return a.Qno - b.Qno;
                        });
                    setQuestionItems(questionData);
                }

                if (storedScores || storedReviews) {
                    const nextScores = normalizeScores(storedScores ?? []);
                    const nextReviews = Array.isArray(storedReviews) ? storedReviews : [];
                    setDraftExamScores(nextScores);
                    setDraftExamReviews(nextReviews);
                    setSavedExamScores(nextScores);
                    setSavedExamReviews(nextReviews);
                    setSaveStatus('saved');
                } else if (shikakuRef) {
                    const initialReviews = questionData.length
                        ? [
                              {
                                  chapter: questionData[0].Chapter,
                                  qno: questionData[0].Qno,
                                  explanation: questionData[0].Explanation || '',
                              },
                          ]
                        : [{ chapter: 1, qno: 1, explanation: '' }];
                    const initialScores = normalizeScores(createDefaultScores(SCORE_ROW_COUNT));
                    setDraftExamReviews(initialReviews);
                    setDraftExamScores(initialScores);
                    setSavedExamReviews([]);
                    setSavedExamScores([]);
                    setSaveStatus('draft');
                } else {
                    setDraftExamScores(normalizeScores(createDefaultScores(SCORE_ROW_COUNT)));
                    setDraftExamReviews([{ chapter: 1, qno: 1, explanation: '' }]);
                }
            } catch (err: any) {
                console.error('Error fetching order exam data:', err);
                setError(err?.message || 'Orderの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [id]);

    const savedTotals = useMemo(() => sumScores(savedExamScores), [savedExamScores]);

    const scoreComment = useMemo(() => {
        if (!shikakuData) return '';
        const score = savedTotals.score;

        if (score >= 100) return shikakuData.Lv100 || '';
        if (score >= 95) return shikakuData.Lv95 || '';
        if (score >= 90) return shikakuData.Lv90 || '';
        if (score >= 80) return shikakuData.Lv80 || '';
        if (score >= 70) return shikakuData.Lv70 || '';
        return shikakuData.Lv1 || '';
    }, [savedTotals.score, shikakuData]);

    const handleScoreChange = (index: number, field: keyof ExamScore, value: string) => {
        setDraftExamScores((prev) =>
            prev.map((item, idx) => {
                if (idx !== index) return item;
                if (field === 'label') {
                    return { ...item, label: value };
                }
                if (value === '') {
                    return { ...item, [field]: null };
                }
                const numeric = Number(value);
                return { ...item, [field]: Number.isNaN(numeric) ? null : numeric };
            })
        );
        setSaveStatus('draft');
    };

    const handleReviewChange = (index: number, field: keyof ExamReview, value: string | number) => {
        setDraftExamReviews((prev) =>
            prev.map((item, idx) => {
                if (idx !== index) return item;
                if (field === 'chapter' || field === 'qno') {
                    const numeric = typeof value === 'number' ? value : Number(value);
                    const nextItem = { ...item, [field]: Number.isNaN(numeric) ? 0 : numeric };
                    const matchedQuestion = questionItems.find(
                        (question) => question.Chapter === nextItem.chapter && question.Qno === nextItem.qno
                    );
                    if (matchedQuestion) {
                        return {
                            ...nextItem,
                            explanation: matchedQuestion.Explanation || '',
                            ...(typeof item.explanationHeight === 'number'
                                ? { explanationHeight: item.explanationHeight }
                                : {}),
                        };
                    }
                    return nextItem;
                }
                return { ...item, [field]: value };
            })
        );
        setSaveStatus('draft');
    };

    const handleReviewHeightChange = (index: number, height: number) => {
        setDraftExamReviews((prev) =>
            prev.map((item, idx) => (idx === index ? { ...item, explanationHeight: height } : item))
        );
        setSaveStatus('draft');
    };

    const handleRemoveReviewRow = () => {
        setDraftExamReviews((prev) => prev.slice(0, -1));
        setSaveStatus('draft');
    };

    const handleRemoveReviewRowAt = (index: number) => {
        setDraftExamReviews((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
        setSaveStatus('draft');
    };

    const handleMoveReviewRow = (index: number, direction: 'up' | 'down') => {
        setDraftExamReviews((prev) => {
            const nextIndex = direction === 'up' ? index - 1 : index + 1;
            if (nextIndex < 0 || nextIndex >= prev.length) return prev;
            const next = [...prev];
            const [moved] = next.splice(index, 1);
            next.splice(nextIndex, 0, moved);
            return next;
        });
        setSaveStatus('draft');
    };

    const handleToggleReviewPageBreak = (index: number) => {
        setDraftExamReviews((prev) =>
            prev.map((item, rowIndex) =>
                rowIndex === index ? { ...item, pageBreakBefore: !item.pageBreakBefore } : item
            )
        );
        setSaveStatus('draft');
    };

    const handleAddReviewRow = () => {
        setDraftExamReviews((prev) => [
            ...prev,
            { chapter: EXAM_CHAPTER_OPTIONS[0], qno: EXAM_QNO_OPTIONS[0], explanation: '' },
        ]);
        setSaveStatus('draft');
    };

    const handlePrint = (mode: PrintMode) => {
        setPrintMode(mode);
        setIsPrintMenuOpen(false);
        if (mode === 'letter') {
            setActiveTab('letter');
        }
        if (mode === 'main') {
            setActiveTab('exam');
        }
        setTimeout(() => window.print(), 100);
    };

    useEffect(() => {
        if (hasAutoPrinted.current || loading || error) {
            return;
        }
        const printModeParam = searchParams.get('print');
        if (printModeParam === 'letter' || printModeParam === 'main' || printModeParam === 'all') {
            hasAutoPrinted.current = true;
            handlePrint(printModeParam);
        }
    }, [loading, error, searchParams, handlePrint]);

    const handleSave = async () => {
        if (!id || saving) return;
        setSaving(true);
        try {
            const sanitizedExamReviews = sanitizeExamReviews(draftExamReviews);
            await updateDoc(doc(db, 'Order', id), {
                examScores: draftExamScores,
                examReviews: sanitizedExamReviews,
                examScoredAt: Timestamp.now(),
            });
            setSavedExamScores(draftExamScores);
            setSavedExamReviews(sanitizedExamReviews);
            setDraftExamReviews(sanitizedExamReviews);
            setSaveStatus('saved');
            window.location.reload();
        } catch (err: any) {
            console.error('Error updating order exam data:', err);
            alert(err?.message || '本試験の更新に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    const draftTotals = useMemo(() => sumScores(draftExamScores), [draftExamScores]);
    const scoredAtLabel = useMemo(() => {
        if (!orderData?.examScoredAt?.toDate) return '-';
        return orderData.examScoredAt.toDate().toLocaleDateString('ja-JP');
    }, [orderData?.examScoredAt]);
    const letterDateLabel = scoredAtLabel === '-' ? new Date().toLocaleDateString('ja-JP') : scoredAtLabel;
    const letterRecipient = userName || '受講者';
    const shikakuName = shikakuData?.ShikakuName || '資格';

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">読み込み中...</div>;
    }

    if (error) {
        return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
    }

    return (
        <div
            className={`order-task-print print-mode-${printMode}`}
            style={{
                ['--order-print-page-margin-top-bottom' as const]: `${orderPrintSettings.pageMarginTopBottomMm}mm`,
                ['--order-print-page-padding-top' as const]: `${orderPrintSettings.pagePaddingTopMm}mm`,
                ['--order-print-page-padding-bottom' as const]: `${orderPrintSettings.pagePaddingBottomMm}mm`,
                ['--order-print-review-font-size' as const]: `${orderPrintSettings.reviewFontSizePt}pt`,
                ['--order-print-review-cell-padding' as const]: `${orderPrintSettings.reviewCellPaddingPx}px`,
                ['--order-print-review-textarea-padding' as const]: `${orderPrintSettings.reviewTextareaPaddingPx}px`,
                ['--order-print-task-letter-title-font-size' as const]: `${orderPrintSettings.taskLetterTitleFontSizePt}pt`,
                ['--order-print-exam-letter-title-font-size' as const]: `${orderPrintSettings.examLetterTitleFontSizePt}pt`,
            }}>
            <div className="print-toolbar">
                <div className="print-breadcrumbs">
                    <Link to="/admin/orders">一覧</Link>
                    <span>&gt;</span>
                    <Link to={`/admin/orders/${id}`}>注文詳細</Link>
                    <span>&gt;</span>
                    <span className="current">本試験</span>
                </div>
                <div className="print-actions">
                    <div className="print-menu">
                        <button
                            type="button"
                            onClick={() => setIsPrintMenuOpen((prev) => !prev)}
                            className="print-button"
                        >
                            印刷
                        </button>
                        {isPrintMenuOpen && (
                            <div className="print-menu-list">
                                <button type="button" onClick={() => handlePrint('all')}>
                                    両方印刷
                                </button>
                                <button type="button" onClick={() => handlePrint('main')}>
                                    本試験のみ印刷
                                </button>
                                <button type="button" onClick={() => handlePrint('letter')}>
                                    送付状のみ印刷
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="print-tabs">
                <button
                    type="button"
                    className={activeTab === 'exam' ? 'is-active' : ''}
                    onClick={() => setActiveTab('exam')}
                >
                    本試験
                </button>
                <button
                    type="button"
                    className={activeTab === 'letter' ? 'is-active' : ''}
                    onClick={() => setActiveTab('letter')}
                >
                    送付状
                </button>
            </div>

            <div className={`tab-panel ${activeTab === 'exam' ? 'is-active' : ''}`} data-print-target="main">
                <div className="a4-page">
                    <header>
                        <h1>本試験採点結果</h1>
                    </header>

                    <section className="info-table">
                        <table>
                            <tbody>
                                <tr>
                                    <th>氏名</th>
                                    <td>{userName || '-'}</td>
                                    <th>採点日</th>
                                    <td>{scoredAtLabel}</td>
                                </tr>
                                <tr>
                                    <th>受験資格名</th>
                                    <td colSpan={3}>{shikakuData?.ShikakuName || '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section>
                        <div className="section-header">
                            <h2>採点結果内訳</h2>
                        </div>
                        <div className="total-score">
                            合計得点: <span>{draftTotals.score}点</span> / {draftTotals.max}点満点
                        </div>
                        <table className="score-table">
                            <thead>
                                <tr>
                                    <th className="center">項目</th>
                                    <th className="center">得点</th>
                                    <th className="center">配点</th>
                                </tr>
                            </thead>
                            <tbody>
                                {draftExamScores.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="center">
                                            採点項目がありません
                                        </td>
                                    </tr>
                                )}
                                {draftExamScores.map((scoreItem, index) => (
                                    <tr key={`${scoreItem.label}-${index}`}>
                                        <td className="center">
                                            <input
                                                type="text"
                                                value={scoreItem.label}
                                                onChange={(e) => handleScoreChange(index, 'label', e.target.value)}
                                            />
                                        </td>
                                        <td className="center">
                                            <div className="score-input">
                                                <input
                                                    type="number"
                                                    max={DEFAULT_SCORE_MAX}
                                                    value={scoreItem.score ?? ''}
                                                    onChange={(e) => handleScoreChange(index, 'score', e.target.value)}
                                                />
                                                <span>点</span>
                                            </div>
                                        </td>
                                        <td className="center">
                                            <div className="score-input">
                                                <input
                                                    type="number"
                                                    max={DEFAULT_SCORE_MAX}
                                                    value={scoreItem.max}
                                                    onChange={(e) => handleScoreChange(index, 'max', e.target.value)}
                                                />
                                                <span>点</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section ref={reviewContainerRef}>
                        <div className="section-header">
                            <h2>項目解説</h2>
                            <div className="section-actions">
                                <button onClick={handleSave} disabled={saving} className="print-save-button">
                                    {saving ? '保存中...' : saveStatus === 'saved' ? '更新済み' : '更新'}
                                </button>
                                <button type="button" onClick={handleAddReviewRow} className="link-button">
                                    行追加
                                </button>
                                <button type="button" onClick={handleRemoveReviewRow} className="link-button">
                                    行削除
                                </button>
                            </div>
                        </div>
                        <table className="review-table">
                            <colgroup>
                                <col className="review-col-number" />
                                <col className="review-col-detail" />
                                <col className="review-col-actions" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="center">番号</th>
                                    <th>解説</th>
                                    <th className="center review-actions" aria-label="操作" />
                                </tr>
                            </thead>
                            <tbody>
                                {draftExamReviews.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="center">
                                            レビュー項目がありません
                                        </td>
                                    </tr>
                                )}
                                {draftExamReviews.map((review, index) => (
                                    <tr key={`${review.chapter}-${review.qno}-${index}`} className={review.pageBreakBefore ? 'review-page-break' : undefined}>
                                        <td className="center">
                                            <div className="review-number">
                                                <span className="review-number-text">
                                                    {review.chapter}-{review.qno}
                                                </span>
                                                <select
                                                    value={review.chapter}
                                                    onChange={(e) =>
                                                        handleReviewChange(index, 'chapter', Number(e.target.value))
                                                    }
                                                >
                                                    {EXAM_CHAPTER_OPTIONS.map((chapter) => (
                                                        <option key={chapter} value={chapter}>
                                                            {chapter}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="review-separator">-</span>
                                                <select
                                                    value={review.qno}
                                                    onChange={(e) => handleReviewChange(index, 'qno', Number(e.target.value))}
                                                >
                                                    {EXAM_QNO_OPTIONS.map((qno) => (
                                                        <option key={qno} value={qno}>
                                                            {qno}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="review-detail">
                                                <textarea
                                                    data-auto-resize
                                                    data-saved-height={review.explanationHeight || undefined}
                                                    rows={3}
                                                    value={review.explanation}
                                                    onChange={(e) =>
                                                        handleReviewChange(index, 'explanation', e.target.value)
                                                    }
                                                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                                    onMouseUp={(e) =>
                                                        handleReviewHeightChange(index, e.currentTarget.offsetHeight)
                                                    }
                                                    onTouchEnd={(e) =>
                                                        handleReviewHeightChange(index, e.currentTarget.offsetHeight)
                                                    }
                                                    onBlur={(e) =>
                                                        handleReviewHeightChange(index, e.currentTarget.offsetHeight)
                                                    }
                                                    style={{
                                                        height: review.explanationHeight
                                                            ? `${review.explanationHeight}px`
                                                            : undefined,
                                                    }}
                                                />
                                            </div>
                                        </td>
                                        <td className="review-actions center">
                                            <div className="review-action-buttons">
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleReviewPageBreak(index)}
                                                    className="review-page-break-button"
                                                >
                                                    {review.pageBreakBefore ? '改ページ解除' : '改ページ'}
                                                </button>
                                                <div className="review-inline-actions">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveReviewRow(index, 'up')}
                                                        disabled={index === 0}
                                                        aria-label="上へ移動"
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveReviewRow(index, 'down')}
                                                        disabled={index === draftExamReviews.length - 1}
                                                        aria-label="下へ移動"
                                                    >
                                                        ↓
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveReviewRowAt(index)}
                                                        className="review-delete-button"
                                                        aria-label="行を削除"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section>
                        <h2>評価者によるコメント</h2>
                        <div className="comment-box">
                            <p>{scoreComment || 'コメントがありません'}</p>
                        </div>
                    </section>
                </div>
            </div>

            <div className={`tab-panel ${activeTab === 'letter' ? 'is-active' : ''}`} data-print-target="letter">
                <div className="a4-page letter-page exam-letter">
                    <div className="header-meta exam-letter-meta">
                        <div className="recipient">{letterRecipient} 様</div>
                        <div className="sender-info">
                            {letterDateLabel}
                            <br />
                            一般社団法人日本技能開発協会
                            <br />
                            京都市下京区大黒町227
                            <br />
                            第二キョートビル4F
                            <br />
                            E-mail:info@jsada.org
                        </div>
                    </div>

                    <header className="exam-letter-title">
                        <h1>
                            {shikakuName}資格
                            <br />
                            認定証書の交付のお知らせ
                        </h1>
                    </header>

                    <main>
                        <p>拝啓</p>
                        <p>
                            時下ますますご清栄のこととお喜び申し上げます。{shikakuName}
                            資格を受験頂きまして誠にありがとうございます。
                        </p>
                        <p>
                            採点させて頂いた結果、今回の試験に見事合格されましたので、認定証書を交付いたします。
                        </p>
                        <p>
                            本認定証書（DIPLOMA）については知識習得の証左として、履歴書や経歴書にご記載頂けます。
                            また、更新等の期限もございませんので、永くご活躍されることを願っております。
                        </p>
                        <p>
                            この度は合格誠におめでとうございます。心より御祝い申し上げます。引き続き、当協会へのご愛顧を賜りますようお願い申し上げ、交付のお知らせと代えさせて頂きます。
                        </p>
                        <p className="exam-letter-closing">敬具</p>
                    </main>
                </div>
            </div>
        </div>
    );
}
